<template>
  <div class="sync-log" v-loading="loading">
    <div class="page-header">
      <div>
        <h2 class="page-title">同步日志</h2>
        <p class="page-desc">
          记录每次数据同步运行结果（实时行情轮询 / CSV 导入），保留 {{ retainDays }} 天
        </p>
      </div>
    </div>

    <div class="toolbar">
      <span class="toolbar-label">任务</span>
      <el-select
        v-model="filterJob"
        size="small"
        clearable
        placeholder="全部任务"
        style="width: 150px"
        @change="onFilterChange"
      >
        <el-option
          v-for="(label, job) in JOB_LABELS"
          :key="job"
          :label="label"
          :value="job"
        />
      </el-select>

      <span class="toolbar-label">状态</span>
      <el-select
        v-model="filterStatus"
        size="small"
        clearable
        placeholder="全部状态"
        style="width: 130px"
        @change="onFilterChange"
      >
        <el-option
          v-for="(label, status) in STATUS_LABELS"
          :key="status"
          :label="label"
          :value="status"
        />
      </el-select>

      <el-button type="primary" size="small" :loading="loading" @click="onQueryClick">
        查询
      </el-button>
      <el-button size="small" @click="onRefresh">刷新</el-button>
    </div>

    <div v-if="summary" class="summary-bar">
      <span class="summary-item">近 7 天：</span>
      <el-tag size="mini" type="success">成功 {{ summary.success }}</el-tag>
      <el-tag size="mini" type="warning">部分失败 {{ summary.partial }}</el-tag>
      <el-tag size="mini" type="danger">失败 {{ summary.failed }}</el-tag>
      <el-tag size="mini" type="info">跳过 {{ summary.skipped }}</el-tag>
    </div>

    <el-table v-if="total > 0" :data="rows" style="width: 100%">
      <el-table-column type="index" label="序号" width="70" :index="indexMethod" />
      <el-table-column label="任务" width="120">
        <template slot-scope="{ row }">
          {{ JOB_LABELS[row.job] || row.job }}
        </template>
      </el-table-column>
      <el-table-column label="状态" width="90">
        <template slot-scope="{ row }">
          <el-tag :type="STATUS_TAG_TYPES[row.status] || 'info'" size="mini">
            {{ STATUS_LABELS[row.status] || row.status }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="成功/总数" width="100">
        <template slot-scope="{ row }">
          {{ row.success != null ? `${row.success}/${row.total != null ? row.total : '-'}` : '-' }}
        </template>
      </el-table-column>
      <el-table-column label="失败" width="70">
        <template slot-scope="{ row }">
          <span :class="{ 'fail-count': (row.failCount || 0) > 0 }">
            {{ row.failCount != null ? row.failCount : '-' }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="耗时" width="90">
        <template slot-scope="{ row }">
          {{ formatDuration(row.durationMs) }}
        </template>
      </el-table-column>
      <el-table-column prop="mode" label="模式" width="90">
        <template slot-scope="{ row }">
          {{ MODE_LABELS[row.mode] || row.mode || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="时间" width="170">
        <template slot-scope="{ row }">
          {{ formatTime(row.runAt) }}
        </template>
      </el-table-column>
      <el-table-column prop="message" label="详情" min-width="300" show-overflow-tooltip />
    </el-table>

    <div v-else-if="!loading" class="empty-tip">暂无同步记录</div>

    <el-pagination
      v-if="total > 0"
      class="pager"
      background
      layout="total, sizes, prev, pager, next, jumper"
      :current-page.sync="page"
      :page-size.sync="pageSize"
      :page-sizes="pageSizeOptions"
      :total="total"
      @current-change="loadLogs"
      @size-change="onPageSizeChange"
    />
  </div>
</template>

<script>
import { fetchSyncLogs } from '../utils/syncLogApi.js'

const JOB_LABELS = {
  realtime_top500: 'Top500 实时',
  realtime_universe: '全市场实时',
  realtime_index: '指数实时',
  realtime_manual: '手动同步',
  csv_import: 'CSV 导入',
}

const STATUS_LABELS = {
  success: '成功',
  partial: '部分失败',
  failed: '失败',
  skipped: '跳过',
}

const STATUS_TAG_TYPES = {
  success: 'success',
  partial: 'warning',
  failed: 'danger',
  skipped: 'info',
}

const MODE_LABELS = {
  trading: '交易时段',
  auction: '集合竞价',
  idle: '非交易',
  auto: '自动',
  manual: '手动',
}

export default {
  name: 'SyncLog',
  data() {
    return {
      loading: false,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      pageSizeOptions: [10, 20, 50, 100],
      filterJob: '',
      filterStatus: '',
      summary: null,
      retainDays: 30,
      JOB_LABELS,
      STATUS_LABELS,
      STATUS_TAG_TYPES,
      MODE_LABELS,
    }
  },
  created() {
    this.loadLogs()
  },
  methods: {
    indexMethod(index) {
      return (this.page - 1) * this.pageSize + index + 1
    },
    onFilterChange() {
      this.page = 1
      this.loadLogs()
    },
    onQueryClick() {
      this.page = 1
      this.loadLogs()
    },
    onRefresh() {
      this.loadLogs()
    },
    onPageSizeChange() {
      this.page = 1
      this.loadLogs()
    },
    async loadLogs() {
      this.loading = true
      try {
        const data = await fetchSyncLogs({
          job: this.filterJob,
          status: this.filterStatus,
          page: this.page,
          pageSize: this.pageSize,
        })
        this.rows = data.rows || []
        this.total = data.total || 0
        this.page = data.page || this.page
        this.pageSize = data.pageSize || this.pageSize
        this.summary = data.summary || null
        if (data.retainDays) this.retainDays = data.retainDays
      } catch (err) {
        this.rows = []
        this.total = 0
        this.$message.error(err.message || '加载同步日志失败')
      } finally {
        this.loading = false
      }
    },
    formatTime(value) {
      if (!value) return '-'
      const normalized = String(value).includes('T') ? value : value.replace(' ', 'T')
      const date = new Date(normalized)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleString('zh-CN', { hour12: false })
    },
    formatDuration(ms) {
      if (ms == null) return '-'
      if (ms < 1000) return `${ms}ms`
      if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
      return `${Math.floor(ms / 60000)}m${Math.round((ms % 60000) / 1000)}s`
    },
  },
}
</script>

<style scoped>
.page-header {
  margin-bottom: 16px;
  text-align: left;
}

.page-title {
  margin: 0 0 4px;
  font-size: 20px;
  color: #303133;
}

.page-desc {
  margin: 0;
  font-size: 13px;
  color: #909399;
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.toolbar-label {
  font-size: 14px;
  color: #606266;
}

.summary-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;
}

.fail-count {
  color: #f56c6c;
  font-weight: 600;
}

.pager {
  margin-top: 16px;
  text-align: right;
}

.empty-tip {
  padding: 24px 0;
  color: #909399;
  text-align: center;
}
</style>
